import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCheckCircle, FaAdjust, FaDotCircle, FaRegCircle,
  FaJava, FaJs, FaPython
} from "react-icons/fa";

const STATUS_CONFIG = {
  solved: { label: "Solved", icon: <FaCheckCircle />, cls: "solved" },
  partially_solved: { label: "Partial", icon: <FaAdjust />, cls: "partial" },
  attempted: { label: "Attempted", icon: <FaDotCircle />, cls: "attempted" },
  not_attempted: { label: "", icon: <FaRegCircle />, cls: "unattempted" },
};

const LANG_ICONS = {
  Java: <FaJava />,
  JavaScript: <FaJs />,
  Python: <FaPython />,
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const PracticeQuestionCard = ({ question, index }) => {
  const navigate = useNavigate();
  const st = question.practiceStatus || "not_attempted";
  const cfg = STATUS_CONFIG[st] || STATUS_CONFIG.not_attempted;
  const langs = (question.languages || question.categories || []).map(c => typeof c === 'object' ? c.name : c);

  return (
    <div
      className={`pqc-card ${cfg.cls}`}
      onClick={() => navigate(`/s/practice/${question._id}`)}
    >
      {/* Index number */}
      <span className="pqc-index">{index}</span>

      {/* Status indicator — only show when attempted */}
      {st !== "not_attempted" && (
        <div className={`pqc-status-icon ${cfg.cls}`} title={cfg.label}>
          {cfg.icon}
        </div>
      )}

      {/* Main content */}
      <div className="pqc-content">
        <div className="pqc-title-row">
          <span className="pqc-title">{question.title}</span>
        </div>
        <div className="pqc-meta-row">
          {/* Difficulty */}
          <span className={`pqc-diff ${(question.difficulty || "").toLowerCase()}`}>
            {question.difficulty}
          </span>
          {/* Language/tech badges */}
          {langs.map((l) => (
            <span key={l} className="pqc-lang-badge">
              {LANG_ICONS[l] || null} {l}
            </span>
          ))}
          {/* Labels */}
          {(question.labels || []).slice(0, 2).map((l, i) => (
            <span key={`l-${i}`} className="pqc-label-tag">{l}</span>
          ))}
        </div>
      </div>

      {/* Right side — status + attempts */}
      <div className="pqc-right-col">
        {st !== "not_attempted" && (
          <span className={`pqc-status-pill ${cfg.cls}`}>
            {cfg.icon} {cfg.label}
            {question.bestScore && <span className="pqc-best-score">{question.bestScore}</span>}
          </span>
        )}
        {question.bestScoreOutOf100 !== null && question.bestScoreOutOf100 !== undefined && (
          <span className={`pqc-score-100 ${question.bestScoreOutOf100 === 100 ? "pqc-score-100--perfect" : question.bestScoreOutOf100 >= 50 ? "pqc-score-100--good" : "pqc-score-100--low"}`}>
            {question.bestScoreOutOf100}/100
          </span>
        )}
        {question.attemptCount > 0 && (
          <span className="pqc-attempt-info">
            {question.attemptCount} attempt{question.attemptCount !== 1 ? "s" : ""}
            {question.lastAttemptedAt && (
              <span className="pqc-time-ago"> · {timeAgo(question.lastAttemptedAt)}</span>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default PracticeQuestionCard;
